'use client'
// 全局store，用于存储全局状态，请务必谨慎使用，避免滥用。如需要添加全局状态，请先与Edwin确认是否有必要。
import React, { createContext, memo, useContext, useEffect, useRef } from 'react'
import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import locales from 'core/config/locale.json'
import { defaultColor, darkColor, trendReverseColor } from '@/style/colors'
import useFixLegacy from './InitApp/useFixLegacy'
import { TTimezone } from 'core/types'
import { useGetCurrencyListQuery } from 'core/query/yapi/hk-web/Hotcoin/query'
import { useGetCoinInfoListQuery } from 'core/query/yapi/hk-web/HotcoinControllerV3/query'
import _ from 'lodash'
import { useResponsiveDevice } from '@/hooks/useResponsiveDevice'

type LocaleItem = (typeof locales)[number]['value']

export type TrendColor = 'normal' | 'reverse'
export type CurrencyCode =
  | 'CNY'
  | 'USD'
  | 'EUR'
  | 'GBP'
  | 'KRW'
  | 'AED'
  | 'THB'
  | 'JPY'
  | 'AMD'
  | 'ARS'
  | 'AUD'
  | 'BDT'
  | 'HKD'

export type Theme = 'dark' | 'light'
export type TKlineType = 'basic' | 'professional' | 'depth'
/**
 * 图表及对应键值映射
 * Bars: 0,
 * Candles: 1,
 * Line: 2,
 * Area: 3,
 * Renko: 4,
 * Kagi: 5,
 * "Point & figure": 6,
 * "Line Break": 7,
 * "Heiken Ashi": 8,
 * "Hollow Candles": 9,
 * Baseline: 10,
 * "High-low": 12
 * Columns: 13,
 * LineWithMarkers: 14,
 * Stepline: 15,
 "HLC area": 16,
 */
export type TChartType = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 12 | 13 | 14 | 15 | 16

type TAppSetting = {
  version: number
  theme: Theme
  forceTheme: Theme | undefined
  locale: LocaleItem
  currencyCode: CurrencyCode
  currencySymbol: string
  trendColor: TrendColor
  klineType: TKlineType
  chartType: TChartType
  userTradingLayout: 'left' | 'right' | 'normal' // 交易界面布局
  granularity: TTimezone // 涨跌幅基准
}

export type TAppStore = {
  appSetting: TAppSetting
  updateAppSetting: <K extends keyof TAppSetting>(key: K, value: TAppSetting[K]) => void
}

const getDefaultLocale = (): LocaleItem => {
  if (typeof navigator !== 'undefined') {
    return locales.find((item) => item.match?.includes(navigator.language))?.value || 'en_US'
  }
  return 'en_US'
}
const CurrentStoreVersion = 1.1
export const defaultAppSetting: TAppSetting = {
  version: CurrentStoreVersion,
  theme: 'light',
  forceTheme: undefined,
  userTradingLayout: 'right',
  locale: getDefaultLocale(),
  currencyCode: 'USD',
  currencySymbol: '$',
  trendColor: 'normal',
  klineType: 'professional',
  chartType: 1,
  granularity: '',
}

type TContext = ReturnType<ReturnType<typeof create<TAppStore>>>
export const AppStoreContext = createContext<TContext | null>(null)

type TProps = {
  children: React.ReactNode
}

function migrate(oldState: any): TAppSetting {
  if (!oldState.version) {
    // 处理没有 version 的初始版本
    return {
      ...oldState,
      version: 1,
    }
  }

  if (oldState.version === 1) {
    return {
      ...oldState,
      version: CurrentStoreVersion,
    }
  }

  return oldState as TAppSetting
}

export const AppStoreProvider = memo(({ children }: TProps) => {
  const storeRef = useRef<TContext>(
    create(
      persist<TAppStore>(
        (set) => ({
          appSetting: defaultAppSetting,
          updateAppSetting: (key, value) =>
            set((store) => ({
              appSetting: {
                ...store.appSetting,
                [key]: value,
              },
            })),
        }),
        {
          name: 'appStore',
          merge: (persistedState, currentState) => {
            // 数据迁移
            const migratedState = migrate(persistedState)
            return _.merge({}, currentState, migratedState)
          },
        }
      )
    )
  )

  useEffect(() => {
    const currentState = storeRef.current.getState()
    window.document.documentElement.dataset.theme = currentState.appSetting.theme
    window.document.documentElement.dataset.trendColor = currentState.appSetting.trendColor

    return storeRef.current.subscribe((state, prevState) => {
      if (state.appSetting.theme !== prevState.appSetting.theme) {
        window.document.documentElement.dataset.theme = state.appSetting.theme
      }
      if (state.appSetting.trendColor !== prevState.appSetting.trendColor) {
        window.document.documentElement.dataset.trendColor = state.appSetting.trendColor
      }
    })
  }, [])
  useFixLegacy(storeRef)
  return <AppStoreContext.Provider value={storeRef.current}>{children}</AppStoreContext.Provider>
})

AppStoreProvider.displayName = 'AppStoreProvider'

// 注：精确获取对应key可以避免不必要的渲染
export const useAppSetting: <T extends keyof TAppStore['appSetting']>(
  key: T
) => TAppStore['appSetting'][T] | undefined = (key) => {
  const store = useContext(AppStoreContext)
  if (!store) throw new Error('useAppSetting must be used within a AppStoreProvider')
  return store(({ appSetting }) => {
    return appSetting[key]
  })
}

export const useUpdateAppSetting = () => {
  const store = useContext(AppStoreContext)!
  return store((s) => s.updateAppSetting)
}

export const useOnThemeChange = (callback: (theme: Theme) => void, deps?: React.DependencyList) => {
  const store = useContext(AppStoreContext)!
  useEffect(() => {
    return store.subscribe((state, prevState) => {
      if (state.appSetting.theme !== prevState.appSetting.theme) {
        callback(state.appSetting.theme)
      }
    })
  }, deps)
}

export function useColors(variables: (keyof typeof defaultColor)[]) {
  const theme = useAppSetting('theme')
  const trendColor = useAppSetting('trendColor')

  return variables.map((variable) => {
    let color = defaultColor[variable]
    if (theme === 'dark' && darkColor[variable as keyof typeof darkColor]) {
      color = darkColor[variable as keyof typeof darkColor]
    } else if (trendColor === 'reverse' && trendReverseColor[variable as keyof typeof trendReverseColor]) {
      color = trendReverseColor[variable as keyof typeof trendReverseColor]
    }
    return color
  })
}

export function useChartType() {
  const chartType = useAppSetting('chartType') || 1
  const updateAppSetting = useUpdateAppSetting()
  return {
    chartType,
    setChartType: (chartType: TChartType) => {
      updateAppSetting('chartType', chartType)
    },
  }
}

export function useKlineType() {
  const responsiveDevice = useResponsiveDevice()
  const klineType = useAppSetting('klineType')
  const updateAppSetting = useUpdateAppSetting()
  useEffect(() => {
    if (responsiveDevice === 'phone') {
      updateAppSetting('klineType', 'basic')
    }
  }, [responsiveDevice])
  return {
    klineType,
    setKlineType: (klineType: TKlineType) => {
      updateAppSetting('klineType', klineType)
    },
  }
}

/**
 * 自定义Hook: 获取当前选择的货币汇率
 *
 * 该Hook通过以下步骤获取汇率:
 * 1. 从API获取货币列表数据
 * 2. 获取用户当前选择的货币代码
 * 3. 在货币列表中查找匹配的货币项
 * 4. 返回对应的汇率,如果未找到则返回1
 *
 * @example
 * ```tsx
 * function PriceDisplay() {
 *   const rate = useExchangeRate();
 *   return <div>当前汇率: {rate}</div>
 * }
 * ```
 *
 * @returns {number} 当前选择货币的汇率,默认为1
 */
export function useExchangeRate() {
  const { data: currencyData } = useGetCurrencyListQuery()
  const currencyCode = useAppSetting('currencyCode')
  const currencyItem = currencyData?.data?.data.find((item) => item.currencyCode === currencyCode)
  return currencyItem?.exchangeRate || 1
}

export function useCurrencyItem() {
  const { data: currencyData } = useGetCurrencyListQuery()
  const currencyCode = useAppSetting('currencyCode')
  return currencyData?.data?.data.find((item) => item.currencyCode === currencyCode)
}

/**
 * 获取和设置涨跌幅基准粒度的Hook
 * @returns {Object} 返回涨跌幅基准相关的状态和方法
 * @property {string} granularity - 原始涨跌幅基准值(现货推送接口使用如：{"sub":"market.v2.BTC_USDT.24h.tickers.-7"})
 * @property {string} contractGranularity - 格式化后的UTC涨跌幅基准字符串 (例如: UTC+08:00,合约socket接口使用
 *{
 "biz": "perpetual",
 "type": "tickers",
 "zip": false,
 "granularity": "UTC+11:00",
 "serialize": true
 })
 * @property {Function} setGranularity - 更新涨跌幅基准的方法
 */
export function useGranularity() {
  const granularity = useAppSetting('granularity')
  const updateAppSetting = useUpdateAppSetting()
  let contractGranularity
  if (granularity) {
    const absGranularity = Math.abs(Number(granularity))
    const num = absGranularity < 10 ? '0' + absGranularity : absGranularity
    contractGranularity = `UTC${Number(granularity) >= 0 ? '+' : '-'}${num}:00`
  }
  if (granularity === 0) {
    contractGranularity = 'UTC+00:00'
  }
  return {
    granularity,
    contractGranularity,
    setGranularity: (granularity?: TTimezone) => {
      updateAppSetting('granularity', granularity || '')
    },
  }
}

export type TCoin = {
  coinId: number
  shortName: string
  chainName: null | string
  withdraw: boolean
  recharge: boolean
  transfer: boolean
  isUseMemo: boolean
  nameEn: string
  nameZh: string
  webLogo: string
  tradePrecision: number
  calcPrecision: number
  withdrawPrecision: number
  networkName: string
  withdrawMin: string
  isPercentage: true
  withdrawFee: string
  rechargeMinLimit: string
  confirmations: number
  arrivalTime: number
}
/**
 * 获取币种信息
 * @param code
 * code 不传递的情况返回全量列表
 * TODO：后续优化缓存策略
 */
export function useCoinInfo(): Array<TCoin>
export function useCoinInfo(code: string): TCoin | undefined
export function useCoinInfo(code?: string): Array<TCoin> | TCoin | undefined {
  const { data: coinInfoList } = useGetCoinInfoListQuery()
  const coinData: Array<TCoin> = coinInfoList?.data?.data || []
  if (code) {
    return coinData.find((item) => item.shortName?.toUpperCase() === code.toUpperCase())
  }
  return coinData
}
